import { useEffect, useRef, useState } from "react";
import { useApp } from "../../../../context/AppContext";

/** One service inside a suggested slot (its own provider + times). */
export interface SuggestedService {
  salon_service_id: string;
  staff_id: string;
  staff_service_id: string;
  staff_name: string;
  start_dt: string;
  end_dt: string;
  duration_mins: number;
  price: number;
}

/** One server-suggested start slot (GET /suggested-times). */
export interface SuggestedSlot {
  start_dt: string;
  end_dt: string;
  /** Aligned with the requested services (same order). */
  staff_service_ids: string[];
  staff_name: string;
  duration_mins: number;
  price: number;
  /** Per service, in the requested order. */
  services: SuggestedService[];
  /** True for the requested start_time (returned first when it fits). */
  requested?: boolean;
}

interface SuggestedTimesQuery {
  /** Salon services to fit, in booking order; empty = no query. */
  serviceIds: string[];
  /** Providers aligned with serviceIds ("" = any qualified provider). */
  staffIds: string[];
  /** Requested start of the first service, salon-local "HH:mm". */
  startTime?: string;
  /** Salon-local day "YYYY-MM-DD"; null = no query. */
  date: string | null;
  timezone: string;
}

/**
 * Server-suggested starts for the services in order (each with its own
 * provider and start) on one salon-local day (AppContext.getSuggestedTimes),
 * in the order returned (closest to `startTime` first when sent). Shared by
 * the booking drawer, the mobile sheet, rebook screen and reschedule section.
 * Datetimes are UTC: parse them with parseApiDateTime.
 */
export function useSuggestedTimes({
  serviceIds,
  staffIds,
  startTime,
  date,
  timezone,
}: SuggestedTimesQuery) {
  const { getSuggestedTimes } = useApp();
  const [suggestions, setSuggestions] = useState<SuggestedSlot[]>([]);
  const [requestedAvailable, setRequestedAvailable] = useState<boolean | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // getSuggestedTimes isn't memoized in AppContext; read it through a ref so
  // the effect's deps are the query inputs only (no refetch per provider render).
  const getSuggestedTimesRef = useRef(getSuggestedTimes);
  getSuggestedTimesRef.current = getSuggestedTimes;

  // Primitive keys so new arrays with the same values don't refetch. Services
  // and providers stay index-aligned (no filtering).
  const servicesKey = serviceIds.join(",");
  const staffKey = staffIds.join(",");

  useEffect(() => {
    if (!servicesKey || !date) {
      setSuggestions([]);
      setRequestedAvailable(null);
      setError(false);
      setLoading(false);
      return;
    }
    const fetchSuggestions = getSuggestedTimesRef.current;
    if (typeof fetchSuggestions !== "function") return;

    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchSuggestions({
      services: servicesKey.split(","),
      staffIds: staffKey.split(","),
      startTime,
      date,
      timezone,
    })
      .then((resp: any) => {
        if (cancelled) return;
        const slots: SuggestedSlot[] = Array.isArray(resp?.suggested_times)
          ? resp.suggested_times
          : [];
        setSuggestions(slots);
        setRequestedAvailable(
          typeof resp?.requested_time_available === "boolean"
            ? resp.requested_time_available
            : null,
        );
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSuggestions([]);
        setRequestedAvailable(null);
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [servicesKey, staffKey, startTime, date, timezone]);

  return { suggestions, requestedAvailable, loading, error };
}
